/**
 * Atom: Select
 * Styled dropdown select with label, error support, and custom chevron.
 * Supports size="sm" (compact kiosk style) and size="md" (default).
 */
import React from 'react'
import { tokens } from '@/design-system/tokens'

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  children?: React.ReactNode
  label?: string
  error?: string
  helperText?: string
  options?: { value: string; label: string }[]
  size?: 'sm' | 'md'
  placeholder?: string
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  helperText,
  options,
  id,
  style,
  size = 'md',
  placeholder,
  children,
  ...rest
}) => {
  const inputId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`

  const isCompact = size === 'sm'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isCompact ? 3 : tokens.spacing[1] }}>
      {label && (
        <label htmlFor={inputId} style={{
          fontSize: isCompact ? '10px' : tokens.typography.fontSize.sm,
          fontWeight: '700',
          color: 'var(--text-muted)',
          fontFamily: tokens.typography.fontFamily.sans,
          textTransform: 'uppercase',
          letterSpacing: isCompact ? '0.07em' : '0.05em',
        }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <style>{`
          select.ml-select-D1b:focus,
          select.ml-select-D1b:focus-visible {
            border-color: var(--brand-gold) !important;
            box-shadow: 0 0 0 3px var(--brand-gold-quiet);
          }
          select.ml-select-D1b.ml-err:focus,
          select.ml-select-D1b.ml-err:focus-visible {
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
          }
        `}</style>
        <select
          id={inputId}
          className={['ml-select-D1b', error ? 'ml-err' : ''].filter(Boolean).join(' ')}
          style={{
            width: '100%',
            padding: isCompact ? '10px 32px 10px 12px' : `${tokens.spacing[2]} 32px ${tokens.spacing[2]} ${tokens.spacing[3]}`,
            borderRadius: tokens.radius.md,
            border: `1px solid ${error ? tokens.color.feedback.error : tokens.color.border.default}`,
            background: tokens.color.background.elevated,
            color: tokens.color.text.primary,
            fontFamily: tokens.typography.fontFamily.sans,
            fontSize: isCompact ? '13px' : 13,
            outline: 'none',
            cursor: 'pointer',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 120ms ease, box-shadow 120ms ease',
            ...style,
          }}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {(options || []).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
          {children}
        </select>
        {/* Custom chevron */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--text-dim)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {error && (
        <span style={{ fontSize: 11, color: tokens.color.feedback.error, lineHeight: 1.4 }}>
          {error}
        </span>
      )}
      {helperText && !error && (
        <span style={{ fontSize: 11, color: tokens.color.text.muted, lineHeight: 1.4 }}>
          {helperText}
        </span>
      )}
    </div>
  )
}
